import { observable, action, toJS } from 'mobx';
import { ALO_API_HOST, COUNTRIES_WITH_INTL_DOMAIN, SHOP_LOCALE } from './constants';
import { InitialUrlSearchParams } from './environment';
import { getLocationInfo } from './location';
import { clearCurrencyCountryParams } from '../templates/_shared/ui-components/ShippingCurrencyModal/ShippingCountryDropdown';
import { getHrefWithoutOrigin } from './helpers';
import { changeLocalization } from './shopify';
import { changeCurrencyCode } from './currency';

export const SHIPPING_MORE_INFO_LINK = '/pages/shipping-info';
const localstoragekeyname = 'alo-users-prefered-country';

const getShippingType = (countryCode: string) => {
  const CountriesWeShipTo = toJS(ShippingCountryState?.supportedCountries)?.map((country) => country.countryCode);
  const shippingType: ShippingType =
    countryCode === 'US'
      ? 'US'
      : CountriesWeShipTo?.includes(countryCode) // [REVISIT] this whole feature can be accomplished by simply checking if it's US or INTL; Unless business wants to introduce an additional logic on this very line.
      ? 'InternationalCountriesWeShipTo'
      : 'DoNotShipTo';
  return shippingType;
};

export type ShippingType = 'US' | 'InternationalCountriesWeShipTo' | 'DoNotShipTo';

export const fetchShippingCountries = async () => {
  const endpoint = `${ALO_API_HOST}/v1/geoip/supported-countries`;
  const response = await fetch(endpoint, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  return response.status === 200 && response.ok ? response.json() : null;
};
const getStoredPreferredCountry = () => localStorage.getItem(localstoragekeyname);
const shopifyThemeCountryVal = window.Shopify?.country?.toUpperCase();
if (!getStoredPreferredCountry() && shopifyThemeCountryVal) {
  localStorage.setItem(localstoragekeyname, shopifyThemeCountryVal);
}
export const ShippingCountryState = observable({
  // shopify also has its own redirect and is faster and more accurate
  // this is already defaulted above with the shopify theme country, doing it agian here for types
  countryCode: getStoredPreferredCountry() ?? shopifyThemeCountryVal ?? 'US',
  supportedCountries: null as null | SupportedCountries[],
  get country() {
    const countryCode = this.countryCode;
    const countryData = this.supportedCountries?.find((country) => country.countryCode === countryCode);
    return countryData
      ? {
          name: countryData.countryName,
          code: countryData.countryCode,
          currencyName: countryData.currency.name,
          currencyCode: countryData.currency.code,
          shippingType: getShippingType(countryData.countryCode),
          dutiesCopy: countryData.priceDisclaimerText,
          locale: countryData,
          localeName: '',
          languages: countryData.languages,
        }
      : null;
  },
});

export const getCurrentCountryCode = () => {
  return toJS(ShippingCountryState.countryCode);
};

const setSupportedCountries = action((countries: SupportedCountries[]) => {
  ShippingCountryState.supportedCountries = countries;
});

const getSupportedCountries = async () => {
  const response = await fetchShippingCountries();

  const isError = !response || response.hasOwnProperty('detail');
  if (isError) {
    // [REVISIT] More robust error handling
    console.log('getCountriesList failed: ', response ? response : 'See the network panel for more details.');
    const localCopyOfSupportedCountries = window.localStorage.getItem('alo-supported-countries');
    if (localCopyOfSupportedCountries) setSupportedCountries(JSON.parse(localCopyOfSupportedCountries));
  } else {
    setSupportedCountries(response.countries);
    window.localStorage.setItem('alo-supported-countries', JSON.stringify(response.countries));
  }
};
getSupportedCountries();

export const setShopifyMarketLocale = (countryCode: string, localeCode: string, onCompleteCallback?: () => void) => {
  return changeLocalization({
    countryCode: countryCode,
    localeCode: localeCode,
    shouldFollowRedirect: true,
    onSuccessCallback: onCompleteCallback,
  });
};

const countryFromURLparam = InitialUrlSearchParams.get('country') || InitialUrlSearchParams.get('setCountry');

const shouldChangeShopifyLocale =
  countryFromURLparam &&
  countryFromURLparam.toUpperCase() !== window.Shopify.country &&
  !InitialUrlSearchParams.get('country');

export const changeCountry = action((newCountry: string | undefined | null, { updateUserPreference = false } = {}) => {
  if (!newCountry || newCountry === ShippingCountryState.countryCode) return false;
  ShippingCountryState.countryCode = newCountry;
  // what are the use-cases where we we don't update "hard"/localstorage
  if (updateUserPreference) {
    localStorage.setItem(localstoragekeyname, ShippingCountryState.countryCode);
    // ? [REVISIT] is there a reason we can't we do this?
    // ? - possibly because the dropdown sends updates onChange vs onSubmit?
    // changeLocalization({ countryCode: newCountry });
  }
});

const countriesWithINTLdomain = COUNTRIES_WITH_INTL_DOMAIN?.map((country) => country.countryCode) || [];

if (countryFromURLparam && !countriesWithINTLdomain.includes(countryFromURLparam.toUpperCase())) {
  if (shouldChangeShopifyLocale) {
    console.log('changing SM locale from url params');

    changeCountry(countryFromURLparam.toUpperCase(), { updateUserPreference: true });
    setShopifyMarketLocale(countryFromURLparam, SHOP_LOCALE, () => {
      clearCurrencyCountryParams();
      window.location.href = getHrefWithoutOrigin();
    });
  } else {
    changeCountry(countryFromURLparam.toUpperCase(), { updateUserPreference: true });
  }
}

if (!ShippingCountryState.countryCode) {
  // this should technically never run since we are defaulting to Shopify's locale
  getLocationInfo().then((data) => {
    const { country_code: currentCountry } = data;
    changeCountry(currentCountry, { updateUserPreference: true });
  });
}

export const getUsersPreferredCountry = () => localStorage.getItem(localstoragekeyname);

// Update the country on tab switch in browser
document.addEventListener('visibilitychange', (e) => {
  if (document.visibilityState === 'visible') {
    syncShopifyLocaleWithUserPreferences();
  }
});

const syncShopifyLocaleWithUserPreferences = async () => {
  const userPreferredCountry = window.localStorage.getItem(localstoragekeyname);
  if (userPreferredCountry && window.Shopify?.country?.toLowerCase() !== userPreferredCountry?.toLowerCase()) {
    setShopifyMarketLocale(userPreferredCountry, SHOP_LOCALE, () => {
      changeCurrencyCode(userPreferredCountry, { updateUserPreference: true });
      changeCountry(userPreferredCountry, { updateUserPreference: true });
    });
  }
};
//sync with the userdefined locale
if (!window.Shopify.Checkout) window.addEventListener('DOMContentLoaded', syncShopifyLocaleWithUserPreferences);

export interface Countries {
  countries: SupportedCountries[];
  restrictions: Restriction[];
  globaleImplementation: string;
}

export interface SupportedCountries {
  countryCode: string;
  countryName: string;
  currency: Currency;
  priceDisclaimerText: PriceDisclaimerText | null;
  languages: [
    {
      name: string;
      locale: string;
      endonymName: string;
    }
  ];
}

export interface Currency {
  code: string;
  name: string;
  symbol: string;
}

export enum PriceDisclaimerText {
  IncludingTaxesDuties = 'Including taxes & duties',
  Null = 'null',
}

export interface Restriction {
  tag: string;
  type: string;
  countries: string[];
}
